import '@/utils/vfs_fonts.js'
import JsBarcode from 'jsbarcode'
import { createPdf } from 'pdfmake/build/pdfmake.min.js'
import { dateDiff } from '@/utils/date'
import {
  makeFooter,
  makePageSize,
  makeSignatureText
} from '@/utils/print_utils'
import { makeOwnerPatientText } from './print_utils'
import { makePriceDetail } from '@/utils/price_calculation'
import { removeWeekday } from '@/utils/convert_string.js'

const makeTreatmentRow = treatmentItem => {
  const subtotal = [
    { text: `¥${treatmentItem.subtotal.toLocaleString()}`, style: 'cellRight' }
  ]
  if (
    treatmentItem.hasOwnProperty('discountRate') &&
    treatmentItem.discountRate !== 0
  ) {
    subtotal.push({
      text: `割引 ${treatmentItem.discountRate}%`,
      style: 'tinyRight'
    })
  }
  if (
    treatmentItem.hasOwnProperty('discountPrice') &&
    treatmentItem.discountPrice !== 0
  ) {
    subtotal.push({
      text: `値引 ¥${treatmentItem.discountPrice.toLocaleString()}`,
      style: 'tinyRight'
    })
  }
  return [
    {
      text: `${treatmentItem.name}${
        treatmentItem.taxExemptFlg ? '（非課税）' : ''
      }`,
      style: 'cellLeft',
      fontFeatures: ['palt']
    },
    { text: treatmentItem.insuranceFlg ? '〇' : '', style: 'cellCenter' },
    {
      text: `¥${treatmentItem.unitPrice.toLocaleString()}`,
      style: 'cellRight'
    },
    { text: treatmentItem.amount, style: 'cellRight' },
    subtotal
  ]
}

const makePrescriptionRow = (treatmentItem, paperSize, ratio) => {
  let prescriptionSvg = null
  if (paperSize === 'A4') {
    prescriptionSvg =
      '<svg width="8" height="15"><rect x="0" y="3" width="0.5" height="7" fill="#3e3e3e"/><rect x="0" y="10" width="8" height="0.5" fill="#3e3e3e"/></svg>'
  } else if (paperSize === 'B5') {
    prescriptionSvg =
      '<svg width="6.72" height="12.6"><rect x="0" y="2.52" width="0.42" height="5.88" fill="#3e3e3e"/><rect x="0" y="8.4" width="6.72" height="0.42" fill="#3e3e3e"/></svg>'
  } else {
    prescriptionSvg =
      '<svg width="5.6" height="10.5"><rect x="0" y="2.1" width="0.35" height="4.9" fill="#3e3e3e"/><rect x="0" y="7.35" width="5.6" height="0.35" fill="#3e3e3e"/></svg>'
  }
  const prescriptionInfoWidth = paperSize === 'A5' ? 490 * ratio : 520 * ratio
  return [
    {
      colSpan: 5,
      columns: [
        { text: '', width: 5 },
        {
          svg: prescriptionSvg,
          width: 8,
          height: 15
        },
        { text: '', width: 5 },
        {
          text: `処方：${treatmentItem.prescription}`,
          style: 'cellLeft',
          fontFeatures: ['palt'],
          width: prescriptionInfoWidth
        }
      ]
    },
    '',
    '',
    '',
    ''
  ]
}

const makeAllTreatmentRows = (
  medicalPayment,
  showPrescription,
  paperSize,
  ratio
) => {
  if (
    medicalPayment.middleCalculateFlg ||
    (medicalPayment.endHospitalizationFlg &&
      !medicalPayment.startHospitalizationFlg)
  ) {
    const result = []
    medicalPayment.treatmentItemsAndTreatmentDates.forEach(v => {
      if (v.hasOwnProperty('treatmentDate')) {
        result.push([
          { text: v.treatmentDate, style: 'cellLeft', fontFeatures: ['palt'] },
          { text: '', style: 'cellCenter' },
          { text: '', style: 'cellRight' },
          { text: '', style: 'cellRight' },
          [{ text: '', style: 'cellRight' }]
        ])
      } else {
        makeTreatmentRowsGroup(
          v,
          showPrescription,
          paperSize,
          ratio
        ).forEach(row => result.push(row))
      }
    })
    return result
  } else {
    const result = []
    medicalPayment.treatmentItems.forEach(v => {
      makeTreatmentRowsGroup(
        v,
        showPrescription,
        paperSize,
        ratio
      ).forEach(row => result.push(row))
    })
    return result
  }
}

export const makeTreatmentRowsGroup = (
  treatmentItem,
  showPrescription,
  paperSize,
  ratio
) => {
  if (treatmentItem.prescription.length && showPrescription) {
    return [
      makeTreatmentRow(treatmentItem),
      makePrescriptionRow(treatmentItem, paperSize, ratio)
    ]
  } else {
    return [makeTreatmentRow(treatmentItem)]
  }
}

// makeDocDefinition で使用される
// 金額表の作成
export const makePriceDetailRows = (
  medicalPayment,
  ratio,
  showBarcode,
  barcodePrefix
) => {
  let priceDetail
  if (
    medicalPayment.middleCalculateFlg ||
    (medicalPayment.endHospitalizationFlg &&
      !medicalPayment.startHospitalizationFlg)
  ) {
    const surgeryCount = medicalPayment.hospitalizationMedicalPayments.filter(
      v => v.surgeryFlg
    ).length
    //※↓2023/03/01 現状途中精算をした場合、途中精算と退院の診療明細で保険適用はできない仕様にしており、
    //  hStartDateとhEndDateを変えても金額は変わらないので、一律で入院開始日・退院日にしています
    const hStartDate = medicalPayment.hospitalization.startDate
    const hEndDate = medicalPayment.hospitalization.endDate
    const hDays = dateDiff(hStartDate, hEndDate, 'days') + 1
    priceDetail = makePriceDetail(
      medicalPayment,
      surgeryCount,
      hDays,
      medicalPayment.treatmentItems
    )
  } else {
    priceDetail = makePriceDetail(
      medicalPayment,
      medicalPayment.surgeryFlg, //← 0 or 1
      1,
      medicalPayment.treatmentItems
    )
  }
  const cellBlank = { text: '' }
  const baseStyle = { alignment: 'right' }
  const emphasisStyle = { ...baseStyle, fontSize: 14 * ratio, color: 'black' }
  let rows = [
    [
      { text: '' },
      { text: '小計', ...baseStyle },
      { text: `¥${priceDetail.totalPrice}`, ...baseStyle }
    ]
  ]
  let columnLength = 2
  if (priceDetail.discountRate !== '0') {
    columnLength = 3
    if (medicalPayment.insuranceType === 'ipet-docomo') {
      rows.push([
        { text: `全体割引 ${priceDetail.discountRate}%`, ...baseStyle },
        cellBlank,
        cellBlank
      ])
    } else {
      rows.push([
        { text: `全体割引 ${priceDetail.discountRate}%`, ...baseStyle },
        { text: '割引適用額', ...baseStyle },
        { text: `¥${priceDetail.rateDiscountedPrice}`, ...baseStyle }
      ])
    }
  }
  if (priceDetail.discountPrice !== '0') {
    columnLength = 3
    rows.push([
      { text: `全体値引 ¥${priceDetail.discountPrice}`, ...baseStyle },
      { text: '値引適用額', ...baseStyle },
      { text: `¥${priceDetail.fixedDiscountedPrice}`, ...baseStyle }
    ])
  }
  let cellBarcode = cellBlank
  if (showBarcode && String(medicalPayment.burdenAmount).length <= 6) {
    columnLength = 3
    const barcodeText =
      barcodePrefix + String(medicalPayment.burdenAmount).padStart(6, '0')
    cellBarcode = {
      image: textToBase64Barcode(barcodeText, {
        width: 3,
        fontSize: 28,
        background: rows.length % 2 === 0 ? '#ffffff' : '#f0f0f0'
      }),
      fit: [100, 100],
      alignment: 'center'
    }
  }
  rows = rows.concat([
    [
      cellBlank,
      { text: `消費税（10%）`, ...baseStyle, fontFeatures: ['palt'] },
      { text: `¥${priceDetail.consumptionTax}`, ...baseStyle }
    ],
    [
      cellBlank,
      { text: '合計', ...baseStyle },
      { text: `¥${priceDetail.taxIncludedPrice}`, ...baseStyle }
    ],
    [
      cellBlank,
      { text: '保険負担額', ...baseStyle },
      { text: `¥${priceDetail.insurancePrice}`, ...baseStyle }
    ],
    [
      { ...cellBarcode },
      { text: '自己負担額', ...emphasisStyle },
      { text: `¥${priceDetail.ownerBill}`, ...emphasisStyle }
    ]
  ])
  if (columnLength === 2) {
    rows = rows.map(row => {
      const [, ...rest] = row
      return rest
    })
  }
  return { rows, columnLength }
}

export const makeDocDefinition = ({
  printOptions,
  clinic,
  clinicImage,
  barcodePrefix = '000000',
  nextReservationInfo = null,
  printData
}) => {
  const {
    paperSize,
    showBarcode,
    showPrescription,
    ...showClinicItems
  } = printOptions
  const ratio = paperSize === 'A4' ? 1 : paperSize === 'B5' ? 0.84 : 0.71
  //↓テーブル内の診療項目内容のwidthは、A4の値を基準にratioで値を算出するとB5の時に紙からはみ出てしまうため下記の値を使用
  const treatmentItemNameWidth =
    paperSize === 'A4' ? 266 : paperSize === 'B5' ? 226 : 161

  // 各印刷内容の作成
  const content = []
  for (const { owner, patient, medicalPayment } of printData) {
    const ownerPatientText = makeOwnerPatientText({ owner, patient })
    const clinicText = makeSignatureText({
      ratio,
      clinic,
      clinicImage,
      ...showClinicItems
    })
    const treatmentRows = makeAllTreatmentRows(
      medicalPayment,
      showPrescription,
      paperSize,
      ratio
    )
    const { rows: priceDetailRows, columnLength } = makePriceDetailRows(
      medicalPayment,
      ratio,
      showBarcode,
      barcodePrefix
    )

    const contentItemHeader = [
      {
        columns: [
          {
            stack: [
              {
                //右端の医院情報のどれかの文字数が長い時に「診療明細」の文字が改行されないようwidthを設定。
                //widthはcolumnsと組み合わせて使用しないと反映されないようなので下記のような書き方をしている。
                columns: [
                  {
                    text: '診療明細',
                    fontSize: 40 * ratio,
                    width: 260 * ratio
                  }
                ]
              },
              ownerPatientText
            ],
            width: '*'
          },
          {
            stack: clinicText,
            width: 'auto',
            margin: [10, 0, 0, 0]
          }
        ],
        ...(content.length > 0 ? { pageBreak: 'before' } : {}) // 改ページ処理
      }
    ]

    const contentItemBody = [
      // 診断名
      {
        text: medicalPayment.diseaseName,
        margin: [0, 15, 0, 0],
        fontFeatures: ['palt']
      },
      // 発症日
      {
        text: `発症日 ${removeWeekday(medicalPayment.onsetDate)}`,
        margin: [0, 5, 0, 0]
      },
      // 診療日
      {
        text: `診療日 ${removeWeekday(medicalPayment.date)}\t担当者: ${
          medicalPayment.staffName
        }`,
        alignment: 'right',
        margin: [0, 10, 0, 0]
      },
      // 診療項目内容のテーブル
      {
        margin: [0, 5, 0, 0],
        layout: 'treatmentItemsTable',
        table: {
          dontBreakRows: true,
          headerRows: 1,
          widths: [
            treatmentItemNameWidth,
            25 * ratio,
            84 * ratio,
            50 * ratio,
            84 * ratio
          ],
          body: [
            [
              { text: '診療項目内容', alignment: 'center' },
              { text: '保険', alignment: 'center' },
              {
                text: '単価（¥）',
                alignment: 'center',
                fontFeatures: ['palt']
              },
              { text: '数量', alignment: 'center' },
              {
                text: '小計（¥）',
                alignment: 'center',
                fontFeatures: ['palt']
              }
            ],
            ...treatmentRows
          ]
        }
      },
      // 金額表
      {
        margin: [0, 5, 0, 0],
        columns: [
          { width: '*', text: '' },
          {
            width: 'auto',
            layout: 'priceDetailTable',
            table: {
              widths:
                columnLength === 2
                  ? ['auto', 'auto']
                  : ['auto', 'auto', 'auto'],
              dontBreakRows: true,
              headerRows: 0,
              body: priceDetailRows
            }
          }
        ]
      },
      // 次回予約
      ...(nextReservationInfo
        ? Object.keys(nextReservationInfo).length > 0
          ? [
              {
                stack: [
                  { text: nextReservationInfo.text, margin: [0, 15, 0, 0] },
                  {
                    text: nextReservationInfo.patientNames,
                    margin: [70 * ratio, 0, 0, 0]
                  }
                ],
                unbreakable: true
              }
            ]
          : [{ text: '次回の予約：未設定', margin: [0, 15, 0, 0] }]
        : [])
    ]

    const contentItem = contentItemHeader.concat(contentItemBody)
    content.push(contentItem)
  }

  return {
    content,
    footer: (currentPage, pageCount) =>
      makeFooter({
        currentPage,
        fontSize: 11 * ratio,
        pageCount
      }),
    styles: {
      cellLeft: { fontSize: 11 * ratio, alignment: 'left' },
      cellCenter: { fontSize: 11 * ratio, alignment: 'center' },
      cellRight: { fontSize: 11 * ratio, alignment: 'right' },
      tinyRight: { fontSize: 8 * ratio, alignment: 'right' }
    },
    defaultStyle: {
      font: 'NotoSansMono',
      fontSize: 12 * ratio,
      color: '#3e3e3e',
      preserveLeadingSpaces: true
    },
    pageSize: makePageSize(printOptions.paperSize),
    pageMargins: [20, 20, 20, 30 + 11 * ratio],
    pageOrientation: 'portrait'
  }
}

// args = {
//   printOptions,
//   clinic,
//   clinicImage,
//   barcodePrefix,
//   nextReservationInfo,
//   printData
// }
export const printMedicalPayment = args => {
  const docDefinition = makeDocDefinition(args)
  const fonts = {
    NotoSansMono: {
      normal: 'NotoSansMonoCJKJPRegular.otf',
      bold: 'NotoSansMonoCJKJPRegular.otf',
      italics: 'NotoSansMonoCJKJPRegular.otf',
      bolditalics: 'NotoSansMonoCJKJPRegular.otf'
    }
  }
  const tableLayouts = {
    treatmentItemsTable: {
      hLineColor: '#3e3e3e',
      vLineColor: '#3e3e3e',
      fillColor: rowIndex => (rowIndex === 0 ? '#f0f0f0' : null)
    },
    priceDetailTable: {
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      paddingLeft: i => (i === 0 ? 5 : 20),
      fillColor: rowIndex => (rowIndex % 2 === 0 ? '#f0f0f0' : null)
    }
  }
  createPdf(docDefinition, tableLayouts, fonts).open()
}

export const textToBase64Barcode = (text, options = {}) => {
  const canvas = document.createElement('canvas')
  JsBarcode(canvas, text, {
    format: 'EAN13',
    font: 'OCRB',
    ...options
  })
  const dataURL = canvas.toDataURL()
  return dataURL
}
